import { AxiosInstance } from 'axios';
import { EventStatus } from 'enum/event-status';
import { BaseApi } from 'services/api/base-api';
import { ApiResult } from 'services/api/result.type';
import { Category } from 'enum/categories';
import { EventDisplay } from 'enum/event-status.enum';
import { TicketSummary, Event } from 'services/api/models';
import { CheckInResult } from '../receipts/receipts.api';
import {
  EventSetupCategoriesResponse,
  UpdateEventSetupCategoriesRequest,
  ApproverPermissionsCategoriesResponse,
  SchoolEventSetupCategoriesResponse,
} from 'types/event-setup';
import {
  ExportEventsResponseDto,
  GetEventCountByCategoryResponse,
  GetEventResponse,
  ListEventsResponse,
  NearbyEventsResponse,
  SaveEventRequest,
  ScheduleEventsResponse,
  ScheduleQueryParams,
} from './dto';
import {
  ReserveTicketsRequest,
  ReserveTicketsResponse,
} from './dto/reserve-tickets.dto';

export type OccurrenceFilter = 'all' | 'upcoming' | 'past';

export interface OrganizerContact {
  userId: string;
  name: string;
  role: string;
  email: string | null;
  phone: string | null;
  submittedThisRequest: boolean;
}

export interface OrganizerContactsResponse {
  available: boolean;
  organization: { id: string; name: string; logoUrl: string | null } | null;
  contacts: OrganizerContact[];
  count: number;
}

export interface EventDayScheduleResponse {
  available: boolean;
  date: string;
  events: ScheduleEventsResponse['events'];
}

export class EventsApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.getEvent = this.getEvent.bind(this);
    this.getOrganizerContacts = this.getOrganizerContacts.bind(this);
    this.getEventDaySchedule = this.getEventDaySchedule.bind(this);
    this.listEvents = this.listEvents.bind(this);
    this.listEventsRequests = this.listEventsRequests.bind(this);
    this.bulkApproverApproveEvents = this.bulkApproverApproveEvents.bind(this);
    this.getEventRequestsNeedingReviewCount = this.getEventRequestsNeedingReviewCount.bind(this);
    this.createEvent = this.createEvent.bind(this);
    this.editEvent = this.editEvent.bind(this);
    this.starEvent = this.starEvent.bind(this);
    this.unstarEvent = this.unstarEvent.bind(this);
    this.approveEvent = this.approveEvent.bind(this);
    this.rejectEvent = this.rejectEvent.bind(this);
    this.cancelEvent = this.cancelEvent.bind(this);
    this.forceApproveEvent = this.forceApproveEvent.bind(this);
    this.denyEvent = this.denyEvent.bind(this);
    this.moveEventToDrafts = this.moveEventToDrafts.bind(this);
    this.submitEventForApproval = this.submitEventForApproval.bind(this);
    this.reserveTickets = this.reserveTickets.bind(this);
    this.getAttendeesDownload = this.getAttendeesDownload.bind(this);
    this.getCheckInAccess = this.getCheckInAccess.bind(this);
    this.scanCheckIn = this.scanCheckIn.bind(this);
    this.lookupCheckIn = this.lookupCheckIn.bind(this);
    this.getCategoryEventCounts = this.getCategoryEventCounts.bind(this);
    this.viewEvent = this.viewEvent.bind(this);
    this.getEventTotals = this.getEventTotals.bind(this);
    this.getEventOccurrences = this.getEventOccurrences.bind(this);
    this.getEventOccurrenceCounts = this.getEventOccurrenceCounts.bind(this);
    this.deleteRecurringSeries = this.deleteRecurringSeries.bind(this);
    this.getSchedule = this.getSchedule.bind(this);
    this.getScheduleExport = this.getScheduleExport.bind(this);
  }

  public async getEvent (id: string): ApiResult<GetEventResponse> {
    return await this.get<GetEventResponse>(`/events/${id}`);
  }

  public async getOrganizerContacts (id: string): ApiResult<OrganizerContactsResponse> {
    return await this.get<OrganizerContactsResponse>(`/events/${id}/organizer-contacts`);
  }

  public async getEventDaySchedule (id: string): ApiResult<EventDayScheduleResponse> {
    return await this.get<EventDayScheduleResponse>(`/events/${id}/day-schedule`);
  }

  public async getCategoryEventCounts (): ApiResult<GetEventCountByCategoryResponse> {
    return await this.get<any>('/events/by-category');
  }

  public async listEvents ({
    page,
    statuses,
    search,
    cursor,
    schoolId,
    orderBy,
    timeFilter,
    categories = [],
    displayFilter,
    includeCardMeta,
  }: {
    page?: number;
    search: string | null;
    statuses?: EventStatus[];
    cursor?: string | null;
    schoolId?: string | null;
    orderBy?: 'views';
    timeFilter?: 'recent_only';
    categories?: Category[];
    displayFilter?: EventDisplay;
    includeCardMeta?: boolean;
  }): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>('/events', {
      params: {
        includeCardMeta,
        page,
        search,
        statuses,
        cursor,
        schoolId,
        orderBy,
        categories,
        timeFilter,
        displayFilter,
      },
    });
  }

  public async getNearbyEvents (params: {
    schoolId: string;
    date: string;
    startTime?: string;
    endTime?: string;
    facilityId?: string;
    excludeEventId?: string;
  }): ApiResult<NearbyEventsResponse> {
    return await this.get<NearbyEventsResponse>('/events/nearby', {
      params,
    });
  }

  public async getSchedule (
    params: ScheduleQueryParams,
  ): ApiResult<ScheduleEventsResponse> {
    return await this.get<ScheduleEventsResponse>('/events/schedule', {
      params: {
        start: params.start,
        end: params.end,
        facilityIds: params.facilityIds?.length ? params.facilityIds.join(',') : undefined,
        setupCategoryIds: params.setupCategoryIds?.length
          ? params.setupCategoryIds.join(',')
          : undefined,
        facilities: params.facilities,
        setups: params.setups,
        conflictsOnly: params.conflictsOnly ? true : undefined,
        search: params.search || undefined,
      },
    });
  }

  public async getScheduleExport (params: ScheduleQueryParams): Promise<Blob | null> {
    const response = await this.get('/events/schedule/export', {
      params: {
        start: params.start,
        end: params.end,
        facilityIds: params.facilityIds?.length ? params.facilityIds.join(',') : undefined,
        setupCategoryIds: params.setupCategoryIds?.length
          ? params.setupCategoryIds.join(',')
          : undefined,
        facilities: params.facilities,
        setups: params.setups,
        conflictsOnly: params.conflictsOnly ? true : undefined,
        search: params.search || undefined,
      },
      responseType: 'blob',
    });

    if (!response.success) return null;

    return new Blob([response.value as any], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  public async listEventsRequests ({
    page,
    pageSize,
    statuses,
    search,
    cursor,
    schoolId,
    filter,
    displayFilter,
    dateFilter,
  }: {
    page?: number;
    pageSize?: number;
    search: string | null;
    statuses?: EventStatus[];
    cursor?: string | null;
    schoolId?: string | null;
    filter?: string;
    displayFilter?: string;
    dateFilter?: 'UPCOMING' | 'PAST';
  }): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>('/events/v2/event-requests', {
      params: {
        page,
        pageSize,
        search,
        statuses,
        cursor,
        schoolId,
        filter,
        displayFilter,
        dateFilter,
      },
    });
  }

  public async bulkApproverApproveEvents (
    eventIds: string[],
  ): ApiResult<{
    succeeded: Array<{ eventId: string }>;
    failed: Array<{ eventId: string; reason: string }>;
  }> {
    return await this.post('/events/v2/event-requests/bulk-approve', { eventIds });
  }

  public async getEventRequestsNeedingReviewCount ({
    schoolId,
  }: {
    schoolId?: string | null;
  }): ApiResult<{ count: number }> {
    return await this.get<{ count: number }>('/events/v2/event-requests/count', {
      params: {
        schoolId,
      },
    });
  }

  public async listMyOrgsRejectedEvents ({
    schoolId,
    pageSize,
  }: {
    schoolId?: string | null;
    pageSize?: number;
  }): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>('/events/v2/my-orgs-rejected', {
      params: {
        schoolId,
        pageSize,
      },
    });
  }

  public async createEvent (
    data: SaveEventRequest,
  ): ApiResult<GetEventResponse> {
    return await this.post<GetEventResponse>('/events', data);
  }

  public async editEvent (
    id: string,
    data: SaveEventRequest,
  ): ApiResult<GetEventResponse> {
    return await this.put<GetEventResponse>(`/events/${id}`, data);
  }

  public async starEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/star`);
  }

  public async unstarEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/unstar`);
  }

  public async approveEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/approve`);
  }

  public async rejectEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/reject`);
  }

  public async cancelEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/cancel`);
  }

  public async forceApproveEvent (id: string, comment?: string): ApiResult<void> {
    return await this.post(`/events/${id}/force-approve`, { comment });
  }

  public async denyEvent (id: string, comment: string): ApiResult<void> {
    return await this.post(`/events/${id}/deny`, { comment });
  }

  public async moveEventToDrafts (id: string, comment?: string): ApiResult<void> {
    return await this.post(`/events/${id}/move-to-drafts`, { comment });
  }

  public async submitEventForApproval (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/submit-for-approval`);
  }

  public async reserveTickets (
    id: string,
    data: ReserveTicketsRequest,
  ): ApiResult<ReserveTicketsResponse> {
    return await this.post(`/events/${id}/reserveTickets`, data);
  }

  public async getAttendeesDownload (id: string): Promise<Blob | null> {
    const response = await this.get(`/events/${id}/attendees/download`, {
      responseType: 'blob',
    });

    if (!response.success)
      return null;


    const blob = new Blob([response.value as any], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    return blob;
  }

  public async getAttendees (
    id: string,
    search: string,
  ): ApiResult<TicketSummary[]> {
    return await this.get(`/events/${id}/attendees?search=${search}`);
  }

  public async getCheckInAccess (
    id: string,
  ): ApiResult<{
    canOrganize: boolean;
    allowWalkInCheckIn?: boolean;
    checkInStateOverride?: 'auto' | 'open' | 'closed';
    checkInWindowState?: 'not_started' | 'open' | 'closed';
    rsvpCount?: number;
    checkInCount?: number;
    checkInOpensAt?: string;
    checkInClosesAt?: string;
  }> {
    return await this.get(`/events/${id}/check-in-access`);
  }

  public async updateCheckInSettings (
    id: string,
    data: {
      allowWalkInCheckIn?: boolean;
      checkInStateOverride?: 'auto' | 'open' | 'closed';
    },
  ): ApiResult<{
    canOrganize: boolean;
    allowWalkInCheckIn?: boolean;
    checkInStateOverride?: 'auto' | 'open' | 'closed';
    checkInWindowState?: 'not_started' | 'open' | 'closed';
    rsvpCount?: number;
    checkInCount?: number;
    checkInOpensAt?: string;
    checkInClosesAt?: string;
  }> {
    return await this.put(`/events/${id}/check-in-settings`, data);
  }

  public async scanCheckIn (
    id: string,
    code: string,
    method: 'self_scan' | 'staff_scan',
    confirmGuest = false,
  ): ApiResult<CheckInResult> {
    return await this.post(`/events/${id}/check-in/scan`, {
      code,
      method,
      confirmGuest,
    });
  }

  public async lookupCheckIn (
    id: string,
    search: string,
  ): ApiResult<{
    allowWalkInCheckIn: boolean;
    rows: Array<{
      userId: string;
      firstName: string;
      lastName: string;
      email: string;
      onRsvpList: boolean;
      isOutsideGuest: boolean;
      isSelf: boolean;
      checkedInAt?: string;
      ticketType?: string;
    }>;
  }> {
    return await this.get(
      `/events/${id}/check-in/lookup?search=${encodeURIComponent(search)}`,
    );
  }

  public async selfCheckIn (id: string): ApiResult<CheckInResult> {
    return await this.post(`/events/${id}/check-in`, {});
  }

  public async viewEvent (id: string): ApiResult<any> {
    return await this.post(`/events/${id}/view`, {});
  }

  public async recordEventImpressions (eventIds: string[]): ApiResult<void> {
    return await this.post('/events/impressions', { eventIds });
  }

  public async getEventTotals (): ApiResult<{
    eventsCount: number;
    eventViewsCount: number;
    usersCount: number;
  }> {
    return await this.get('/events/totals');
  }

  public async getOrganizationEventTotals (): ApiResult<{
    organizations: {
      id: string;
      name: string;
      logoUrl: string;
      count: number;
    }[];
  }> {
    return await this.get('/events/organizations/totals');
  }

  public async getEventStats (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    totalEvents: number;
    upcomingEvents: number;
    pastEvents: number;
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/stats?${params.toString()}`);
  }

  public async getEventDurationStats (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    totalEventHours: number;
    averageEventDuration: number;
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/duration-stats?${params.toString()}`);
  }

  public async getEventByDayOfWeekStats (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    eventsByDayOfWeek: {
      [key: number]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/by-day-of-week?${params.toString()}`);
  }

  public async getEventByTimeOfTheDayStats (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    eventsByTimeOfTheDay: {
      morning: number;
      afternoon: number;
      evening: number;
      lateNight: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/by-time-of-the-day?${params.toString()}`);
  }

  public async getEventByCategories (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    eventsByCategories: {
      [key: string]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/by-categories?${params.toString()}`);
  }

  public async getEventByMonth (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    labels: string[];
    values: number[];
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/by-month?${params.toString()}`);
  }

  public async getEventAttendeesStats (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    totalAttendees: number;
    uniqueAttendees: number;
    averageAttendeesPerEvent: number;
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/attendees-stats?${params.toString()}`);
  }

  public async getEventAttendeesByGender (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
  ): ApiResult<{
    attendeesByGender: {
      [key: string]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    return await this.get(`/events/attendees-by-gender?${params.toString()}`);
  }

  public async getEventAttendeesByClassification (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
  ): ApiResult<{
    attendeesByClassification: {
      [key: string]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    return await this.get(`/events/attendees-by-classification?${params.toString()}`);
  }

  public async getEventAttendeesByMajor (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
  ): ApiResult<{
    attendeesByMajor: {
      [key: string]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    return await this.get(`/events/attendees-by-major?${params.toString()}`);
  }

  public async getEventAttendeesByAge (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
  ): ApiResult<{
    attendeesByAge: {
      [key: string]: number;
    };
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    return await this.get(`/events/attendees-by-age?${params.toString()}`);
  }

  public async getMostAttendedEvents (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    mostAttendedEvents: {
      eventId: string;
      flyerImageUrl: string;
      eventName: string;
      ticketCount: number;
    }[];
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/most-attended-events?${params.toString()}`);
  }

  public async getMostViewedEvents (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    mostViewedEvents: {
      eventId: string;
      flyerImageUrl: string;
      eventName: string;
      viewsCount: number;
    }[];
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/most-viewed-events?${params.toString()}`);
  }

  public async getEventRsvpAndCheckIns (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    totalRsvpCount: number;
    totalCheckInCount: number;
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/rsvp-and-check-ins?${params.toString()}`);
  }

  public async getEventsExport (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
  ): ApiResult<{
    eventsExport: ExportEventsResponseDto[];
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    return await this.get(`/events/export${qs ? `?${qs}` : ''}`);
  }

  public async getMostActiveOrganizations (organizationId?: string, startDate?: string, endDate?: string): ApiResult<{
    mostActiveOrganizations: {
      organizationId: string;
      organizationName: string;
      organizationLogoUrl: string;
      eventsCount: number;
    }[];
  }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/most-active-organizations?${params.toString()}`);
  }

  public async getEventSetupCategories (schoolId?: string): ApiResult<EventSetupCategoriesResponse> {
    return await this.get<EventSetupCategoriesResponse>(`/events/setup-categories?schoolId=${schoolId || ''}`);
  }

  public async getSchoolEventSetupCategoriesApproverPermissions (schoolId?: string): ApiResult<ApproverPermissionsCategoriesResponse> {
    return await this.get<ApproverPermissionsCategoriesResponse>(`/events/school-setup-categories?schoolId=${schoolId || ''}`);
  }

  public async getSchoolEventSetupCategories (): ApiResult<SchoolEventSetupCategoriesResponse> {
    return await this.get<SchoolEventSetupCategoriesResponse>('/events/school-setup-categories-list');
  }

  public async updateEventSetupCategories (data: UpdateEventSetupCategoriesRequest, schoolId: string): ApiResult<EventSetupCategoriesResponse> {
    return await this.put<EventSetupCategoriesResponse>(`/events/setup-categories?schoolId=${schoolId}`, data);
  }

  public async approverApproveEvent (id: string, comment?: string): ApiResult<void> {
    return await this.post(`/events/${id}/approver-approve`, comment ? { comment } : undefined);
  }

  public async approverSequenceApproveEvent (id: string, comment?: string): ApiResult<void> {
    return await this.post(`/events/${id}/approver-sequence-approve`, comment ? { comment } : undefined);
  }

  public async approverSequenceRejectEvent (id: string, comment: string): ApiResult<void> {
    return await this.post(`/events/${id}/approver-sequence-reject`, { comment });
  }

  public async moveToNeedsReviewEvent (id: string): ApiResult<void> {
    return await this.post(`/events/${id}/move-to-needs-review`);
  }

  public async approverRejectEvent (id: string, comment: string): ApiResult<void> {
    return await this.post(`/events/${id}/approver-reject`, { comment });
  }

  public async submitEventFeedback (
    eventId: string,
    rating: number,
    selectedQuestionIds: string[],
    comment?: string,
  ): ApiResult<void> {
    return await this.post(`/events/${eventId}/feedback`, {
      rating,
      selectedQuestionIds,
      comment,
    });
  }

  public async getEventFeedbackStatus (eventId: string): ApiResult<{ hasSubmitted: boolean }> {
    return await this.get(`/events/${eventId}/feedback/status`);
  }

  public async getEventFeedback (eventId: string): ApiResult<{
    id: string;
    eventId: string;
    userId: string;
    rating: number;
    comment: string | null;
    selectedQuestions: Array<{
      id: string;
      question: string;
      displayOrder: number;
    }>;
    createdAt: string;
  }> {
    return await this.get(`/events/${eventId}/feedback`);
  }

  public async getFeedbackQuestions (): ApiResult<{
    questions: Array<{
      id: string;
      question: string;
      displayOrder: number;
    }>;
  }> {
    return await this.get('/feedback/questions');
  }

  public async getEventOccurrences (
    templateId: string,
    filter: OccurrenceFilter = 'all',
  ): ApiResult<Event[]> {
    return await this.get(`/events/${templateId}/occurrences`, {
      params: { filter },
    });
  }

  public async getEventOccurrenceCounts (templateId: string): ApiResult<{
    upcoming: number;
    past: number;
  }> {
    return await this.get(`/events/${templateId}/occurrence-counts`);
  }

  public async deleteRecurringSeries (templateId: string): ApiResult<void> {
    return await this.delete(`/events/${templateId}/series`);
  }

  public async deleteChildOccurrence (occurrenceId: string): ApiResult<void> {
    return await this.delete(`/events/${occurrenceId}/occurrence`);
  }

  public async getEngagementKpis (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<{
    totalUsers: number;
    totalEvents: number;
    eventImpressions: number;
    eventDetailViews: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/engagement-kpis?${params.toString()}`);
  }

  public async getEngagementChart (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<{
    impressions: number[];
    detailViews: number[];
    labels: string[];
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/engagement-chart?${params.toString()}`);
  }

  public async getEventsByLearningOutcomes (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<{
    outcomes: { name: string; count: number }[];
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/by-learning-outcomes?${params.toString()}`);
  }

  public async getAttendeesByResidency (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<{
    attendeesByResidency: { [key: string]: number };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/attendees-by-residency?${params.toString()}`);
  }

  public async getEngagementExport (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<any> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/engagement-export?${params.toString()}`, {
      responseType: 'blob',
    });
  }

  public async getEngagementOverview (
    startDate?: string,
    endDate?: string,
    organizationId?: string,
  ): ApiResult<{
    kpis: {
      totalUsers: number;
      totalEvents: number;
      eventImpressions: number;
      eventDetailViews: number;
    };
    timeCharts: {
      byMonth: { labels: string[]; values: number[] };
      byDayOfWeek: { [key: number]: number };
      byTimeOfDay: {
        morning: number;
        afternoon: number;
        evening: number;
        lateNight: number;
      };
    };
    engagementChart: {
      impressions: number[];
      detailViews: number[];
      labels: string[];
    };
    learningOutcomes: { name: string; count: number }[];
    categories: { [key: string]: number };
    mostViewed: {
      eventId: string;
      flyerImageUrl: string;
      eventName: string;
      viewsCount: number;
    }[];
    mostActiveOrgs: {
      organizationId: string;
      organizationName: string;
      organizationLogoUrl: string;
      eventsCount: number;
    }[];
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/engagement-overview?${params.toString()}`);
  }

  public async getEngagementAudience (
    startDate?: string,
    endDate?: string,
    filters?: {
      classifications?: string[];
      genders?: string[];
      ages?: string[];
      residency?: string[];
      majors?: string[];
    },
    organizationId?: string,
  ): ApiResult<{
    kpis: {
      totalUsers: number;
      totalEvents: number;
      eventImpressions: number;
      eventDetailViews: number;
    };
    attendeesByClassification: { [key: string]: number };
    attendeesByGender: { [key: string]: number };
    attendeesByAge: { [key: string]: number };
    attendeesByResidency: { [key: string]: number };
    attendeesByMajor: { [key: string]: number };
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.classifications?.length)
      filters.classifications.forEach(c => params.append('classifications[]', c));
    if (filters?.genders?.length)
      filters.genders.forEach(g => params.append('genders[]', g));
    if (filters?.ages?.length)
      filters.ages.forEach(a => params.append('ages[]', a));
    if (filters?.residency?.length)
      filters.residency.forEach(r => params.append('residency[]', r));
    if (filters?.majors?.length)
      filters.majors.forEach(m => params.append('majors[]', m));
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/engagement-audience?${params.toString()}`);
  }

  public async getOutcomesKpis (
    startDate?: string,
    endDate?: string,
    organizationId?: string,
  ): ApiResult<{
    totalEvents: number;
    avgLearningOutcomesPerEvent: number;
    avgCompetenciesPerEvent: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/outcomes-kpis?${params.toString()}`);
  }

  public async getOutcomesNaceCoverage (
    startDate?: string,
    endDate?: string,
    organizationId?: string,
  ): ApiResult<{
    competencies: { name: string; eventCount: number; percentage: number }[];
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/outcomes-nace-coverage?${params.toString()}`);
  }

  public async getOutcomesLearningCoverage (
    startDate?: string,
    endDate?: string,
    organizationId?: string,
  ): ApiResult<{
    outcomes: { name: string; naceCompetencies: string[]; eventCount: number; percentage: number }[];
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (organizationId) params.append('organizationId', organizationId);
    return await this.get(`/events/outcomes-learning-coverage?${params.toString()}`);
  }

  public async getOutcomesEventsList (
    startDate?: string,
    endDate?: string,
    organizationId?: string,
    page?: number,
    pageSize?: number,
  ): ApiResult<{
    events: {
      id: string;
      title: string;
      date: string;
      thumbnail: string | null;
      organizations: string[];
      impressions: number;
      learningOutcomes: string[];
      naceCompetencies: string[];
    }[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (organizationId) params.append('organizationId', organizationId);
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    return await this.get(`/events/outcomes-events-list?${params.toString()}`);
  }

  public async getApprovalWorkflowsKpis (
    organizationId?: string,
    startDate?: string,
    endDate?: string,
  ): ApiResult<{ pending: number; rejected: number; approved: number; expired: number }> {
    const params = new URLSearchParams();
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/approval-workflows-kpis?${params.toString()}`);
  }

  public async getApprovalWorkflowsByGroup (
    status: 'pending' | 'rejected' | 'approved' | 'expired',
    organizationId?: string,
    startDate?: string,
    endDate?: string,
  ): ApiResult<{
    groups: { groupKey: string; groupLabel: string; eventCount: number; avgDaysInStep: number }[];
  }> {
    const params = new URLSearchParams();
    params.append('status', status);
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/approval-workflows-by-group?${params.toString()}`);
  }

  public async getApprovalWorkflowsEvents (
    status: 'pending' | 'rejected' | 'approved' | 'expired',
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    page?: number,
    pageSize?: number,
  ): ApiResult<{
    events: {
      id: string;
      shortId: string | null;
      title: string;
      date: string;
      thumbnail: string | null;
      status: string;
      organizations: string[];
      submittedAt: string | null;
      currentStep: string | null;
      daysInStep: number | null;
      rejectedAt: string | null;
      rejectedBy: string | null;
      rejectionReason: string | null;
      approvedAt: string | null;
      timeToApprove: number | null;
      expiredAt: string | null;
    }[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const params = new URLSearchParams();
    params.append('status', status);
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    return await this.get(`/events/approval-workflows-events?${params.toString()}`);
  }

  public async getApprovalWorkflowsExport (
    status: 'pending' | 'rejected' | 'approved' | 'expired',
    organizationId?: string,
    startDate?: string,
    endDate?: string,
  ): ApiResult<any> {
    const params = new URLSearchParams();
    params.append('status', status);
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return await this.get(`/events/approval-workflows-export?${params.toString()}`, {
      responseType: 'blob',
    });
  }

  public async getApprovalWorkflowsOverview (
    status: 'pending' | 'rejected' | 'approved' | 'expired',
    organizationId?: string,
    startDate?: string,
    endDate?: string,
    page?: number,
    pageSize?: number,
  ): ApiResult<{
    kpis: { pending: number; rejected: number; approved: number; expired: number };
    groups: { groupKey: string; groupLabel: string; eventCount: number; avgDaysInStep: number }[];
    events: {
      events: {
        id: string;
        shortId: string | null;
        title: string;
        date: string;
        thumbnail: string | null;
        status: string;
        organizations: string[];
        submittedAt: string | null;
        currentStep: string | null;
        daysInStep: number | null;
        rejectedAt: string | null;
        rejectedBy: string | null;
        rejectionReason: string | null;
        approvedAt: string | null;
        publishedAt: string | null;
        timeToApprove: number | null;
        expiredAt: string | null;
        daysInApprovalFlow: number | null;
      }[];
      total: number;
      page: number;
      pageSize: number;
    };
  }> {
    const params = new URLSearchParams();
    params.append('status', status);
    if (organizationId) params.append('organizationId', organizationId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (page) params.append('page', page.toString());
    if (pageSize) params.append('pageSize', pageSize.toString());
    return await this.get(`/events/approval-workflows-overview?${params.toString()}`);
  }
}
